import { cloneElement, type ReactElement, useEffect, useRef, useState } from 'react';

import { LARGE_ICON_SIZE, SMALL_ICON_SIZE } from '@/constants';
import { cn } from '@/utils/cn';

import type { ButtonVariants } from './button.variants';

export const getIconColor = (variant: ButtonVariants['variant'] = 'primary', disabled: boolean = false): string => {
  if (disabled) {
    return 'text-text-disabled';
  }

  switch (variant) {
    case 'primary':
      return 'text-button-primary-foreground';
    case 'secondary':
    case 'tertiary':
    case 'transparent':
    case 'danger':
    case 'ghost':
      return 'text-text-primary';
    case 'subtle':
      return 'text-text-secondary';
    default:
      return 'text-button-primary-foreground';
  }
};

export const getIconSize = (size: ButtonVariants['size'] = 'medium'): number => {
  return size === 'small' ? SMALL_ICON_SIZE * 4 : LARGE_ICON_SIZE * 4;
};

export const getIconPadding = (isIconChild: boolean, size: ButtonVariants['size'] = 'medium'): string => {
  return (isIconChild && (size === 'small' ? 'p-1.5' : 'p-2')) || '';
};

export const isIconChild = (children: React.ReactNode): boolean => {
  return typeof children !== 'string';
};

export const cloneIconWithProps = (
  icon: ReactElement<{ className?: string; height?: number | string; width?: number | string }> | undefined,
  iconSize: number,
  iconColor: string,
): ReactElement<{ className?: string; height?: number | string; width?: number | string }> | null => {
  if (icon) {
    return cloneElement(icon, {
      width: icon.props.width || iconSize,
      height: icon.props.height || iconSize,
      className: cn(icon.props.className, 'block shrink-0', iconColor),
    });
  }

  return null;
};

export const useButtonLoadingSize = (loading: boolean) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number | string>('auto');
  const [buttonHeight, setButtonHeight] = useState<number | string>('auto');

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
      setButtonHeight(buttonRef.current.offsetHeight);
    }
  }, []);

  return {
    buttonRef,
    buttonStyle: {
      width: loading ? buttonWidth : 'auto',
      height: loading ? buttonHeight : 'auto',
    },
  };
};
